// v2.2 climate data capture — controlled vocabularies (DCIC Field Dictionary
// v2.2, ENUM Library tab). Climate data is held on FACILITIES, not people:
//   DCIC-06  [climate] (livestock_farm) · [biogas] (biogas_system) ·
//            [waste_recovery] (abattoir / slaughter_slab / processing /
//            milk_collection_centre)
//   DCIC-07  update_type = climate_assessment → [climate_assessment]
//   DCIC-11  activity climate_screening + target_facility_id
//   DCIC-12  facility_assessment_ref
// The practice / waste-recovery / manure lists will come from the F9 Climate
// Reference Registry once D-015 is approved — until then these ENUM Library
// values are the placeholders. NLIMS records activity data only; no form here
// computes emissions or percentages (the M&E dashboard does).

const humanize = (value) => String(value)
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

const opts = (values, labels = {}) => values.map((value) => ({ value, label: labels[value] ?? humanize(value) }));

// ── DCIC-06 facility types that open a climate section ─────────────────────
export const LIVESTOCK_FARM_TYPE = 'livestock_farm';
export const BIOGAS_SYSTEM_TYPE = 'biogas_system';
export const WASTE_RECOVERY_FACILITY_TYPES = ['abattoir', 'slaughter_slab', 'processing', 'milk_collection_centre'];

export function climateSectionFor(facilityType) {
  const type = String(facilityType ?? '').toLowerCase();
  if (type === LIVESTOCK_FARM_TYPE) return 'climate';
  if (type === BIOGAS_SYSTEM_TYPE) return 'biogas';
  if (WASTE_RECOVERY_FACILITY_TYPES.includes(type)) return 'waste_recovery';
  return null;
}

// ── DCIC-06 [climate] — livestock farms (F9) ───────────────────────────────
export const CSA_PRACTICE_OPTIONS = opts([
  'none', 'improved_feeding', 'improved_pasture_forage', 'manure_management', 'biogas', 'composting',
  'rotational_grazing', 'silvopasture_agroforestry', 'improved_breeds', 'herd_health_productivity',
  'water_harvesting', 'renewable_energy', 'other',
], { silvopasture_agroforestry: 'Silvopasture / Agroforestry', herd_health_productivity: 'Herd Health & Productivity', improved_pasture_forage: 'Improved Pasture / Forage' });

// IPCC manure-management categories (F9).
export const MANURE_SYSTEM_OPTIONS = opts([
  'pasture_range_paddock', 'daily_spread', 'solid_storage', 'dry_lot', 'liquid_slurry',
  'anaerobic_digester', 'composting', 'burned_for_fuel', 'deep_bedding', 'other',
], { pasture_range_paddock: 'Pasture / Range / Paddock' });

export const FEEDING_REGIME_OPTIONS = opts([
  'grazing_only', 'grazing_plus_supplement', 'crop_residue_based', 'stall_fed_forage', 'concentrate_based', 'mixed',
], { grazing_plus_supplement: 'Grazing + Supplement' });

export const RENEWABLE_ENERGY_OPTIONS = opts(['none', 'solar', 'biogas', 'wind', 'other']);

export const CIRCULARITY_OPTIONS = opts(['conventional', 'partially_circular', 'circular', 'near_net_zero'], {
  near_net_zero: 'Near Net-Zero',
});

// System-set, read-only on DCIC-06.
export const PRACTICE_VERIFICATION_OPTIONS = opts(['pending', 'verified', 'rejected']);
export const MRV_PARTICIPATION_OPTIONS = opts(['not_enrolled', 'enrolled', 'reporting', 'verified']);

// ── DCIC-06 [waste_recovery] — processing facilities (F9) ──────────────────
export const WASTE_RECOVERY_SYSTEM_OPTIONS = opts([
  'none', 'biogas', 'composting', 'rendering', 'blood_meal', 'bone_meal', 'hide_skin_recovery', 'effluent_reuse', 'other',
], { hide_skin_recovery: 'Hide / Skin Recovery' });
export const RECOVERY_STATUS_OPTIONS = opts(['implemented', 'partially_implemented', 'planned', 'none']);
export const WASTE_APPROVAL_OPTIONS = opts(['pending', 'approved', 'rejected']);

// ── DCIC-06 [biogas] — biogas systems ──────────────────────────────────────
export const BIOGAS_HOST_TYPE_OPTIONS = opts(['on_farm', 'processing_facility', 'community', 'institutional']);
export const BIOGAS_FEEDSTOCK_OPTIONS = opts(['cattle_dung', 'poultry_litter', 'pig_slurry', 'abattoir_waste', 'mixed']);
export const BIOGAS_ENERGY_USE_OPTIONS = opts(['cooking', 'lighting', 'electricity', 'heating', 'flaring', 'other']);

// ── DCIC-07 Facility Status Update ─────────────────────────────────────────
export const UPDATE_TYPE_OPTIONS = opts(['status_update', 'climate_assessment']);
export const ASSESSMENT_TYPE_OPTIONS = opts([
  'ghg_baseline', 'emissions_screening', 'csa_verification', 'waste_recovery_verification', 'mrv_verification',
], {
  ghg_baseline: 'GHG Baseline',
  csa_verification: 'CSA Verification',
  mrv_verification: 'MRV Verification',
});
export const ASSESSMENT_SCOPE_OPTIONS = opts(['facility_only', 'state_baseline']);
export const VERIFICATION_RESULT_OPTIONS = opts(['verified', 'partially_verified', 'not_verified']);
export const EMISSIONS_CLASSIFICATION_OPTIONS = opts(['net_reduction', 'emissions_neutral', 'net_increase', 'not_determinable']);
export const ASSESSMENT_OUTCOME_OPTIONS = opts(['pending_approval', 'approved', 'returned']);

export const VERIFICATION_ASSESSMENT_TYPES = ['csa_verification', 'waste_recovery_verification', 'mrv_verification'];
export const REPORT_REQUIRED_ASSESSMENT_TYPES = ['ghg_baseline', 'emissions_screening'];

// ── DCIC-11 activity row ───────────────────────────────────────────────────
export const CLIMATE_SCREENING_OPTIONS = opts(['not_applicable', 'required']);

export function labelOf(options, value) {
  if (value === null || value === undefined || value === '') return '';
  return options.find((option) => option.value === value)?.label ?? humanize(value);
}

export function labelsOf(options, values) {
  return (Array.isArray(values) ? values : []).map((value) => labelOf(options, value)).join(', ');
}

// "none" is a real option in these multi-selects, and it cannot be combined:
// picking it clears everything else, and picking anything else clears it.
export function toggleExclusiveNone(values, value) {
  const current = Array.isArray(values) ? values : [];
  if (current.includes(value)) return current.filter((item) => item !== value);
  if (value === 'none') return ['none'];
  return [...current.filter((item) => item !== 'none'), value];
}
